import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { api, formatApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import { Button } from './ui/Button'
import { Modal } from './ui/Modal'
import { track } from '../lib/analytics'

interface ResumePreview {
  messages: number
  invoices: number
  debtors: number
}

/** Who may press Stop / Resume: owner and manager, never while impersonating. */
export function useCanControlSending(): boolean {
  const { user, isImpersonating } = useAuth()
  return !isImpersonating && (user?.org_role === 'owner' || user?.org_role === 'manager')
}

function PauseModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { refresh } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const pause = useMutation({
    mutationFn: () => api.post('/api/v1/organizations/me/sending/pause', {}),
    onSuccess: async () => {
      track('send_paused')
      await refresh()
      setError(null)
      onClose()
    },
    onError: (err) => setError(formatApiError(err, 'Could not stop sending. Please try again.')),
  })
  return (
    <Modal open={open} onClose={onClose} title="Stop all sending">
      <p className="text-sm text-muted-foreground">
        No customer gets a message from RemindLoop until you resume. Messages we planned do not go out. Billing notices to you are not affected.
      </p>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Keep sending
        </Button>
        <Button variant="danger" disabled={pause.isPending} onClick={() => pause.mutate()}>
          Stop all sending
        </Button>
      </div>
    </Modal>
  )
}

function ResumeModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { refresh } = useAuth()
  const [error, setError] = useState<string | null>(null)
  const preview = useQuery({
    queryKey: ['sending-resume-preview'],
    queryFn: () => api.get<ResumePreview>('/api/v1/organizations/me/sending/resume-preview'),
    enabled: open,
    staleTime: 0,
  })
  const resume = useMutation({
    mutationFn: () => api.post('/api/v1/organizations/me/sending/resume', { confirm: true }),
    onSuccess: async () => {
      track('send_resumed')
      await refresh()
      setError(null)
      onClose()
    },
    onError: (err) => setError(formatApiError(err, 'Could not resume sending. Please try again.')),
  })
  const p = preview.data
  return (
    <Modal open={open} onClose={onClose} title="Resume sending">
      <p className="text-sm text-muted-foreground" data-testid="resume-count">
        {preview.isLoading || !p
          ? 'Checking what is waiting…'
          : p.messages === 0
            ? 'Nothing is waiting. Messages will go out as invoices fall due.'
            : `${p.messages} message${p.messages === 1 ? '' : 's'} to ${p.debtors} customer${
                p.debtors === 1 ? '' : 's'
              } are waiting and will start on the next working day.`}
      </p>
      {error && <p className="mt-2 text-xs text-destructive">{error}</p>}
      <div className="mt-4 flex justify-end gap-2">
        <Button variant="secondary" onClick={onClose}>
          Stay stopped
        </Button>
        <Button disabled={resume.isPending || preview.isLoading} onClick={() => resume.mutate()}>
          Resume sending
        </Button>
      </div>
    </Modal>
  )
}

/** "Stop all sending" when sending is on, "Resume sending" when it is stopped. */
export function SendingToggleButton({ size = 'md' }: { size?: 'sm' | 'md' }) {
  const { user } = useAuth()
  const can = useCanControlSending()
  const [open, setOpen] = useState(false)
  if (!can) return null
  const paused = user?.sending_paused === true
  return (
    <>
      <Button
        size={size}
        variant={paused ? 'primary' : 'secondary'}
        onClick={() => setOpen(true)}
        data-testid="sending-toggle"
      >
        {paused ? 'Resume sending' : 'Stop all sending'}
      </Button>
      {paused ? (
        <ResumeModal open={open} onClose={() => setOpen(false)} />
      ) : (
        <PauseModal open={open} onClose={() => setOpen(false)} />
      )}
    </>
  )
}

/** Shown on every portal page while sending is stopped. */
export function SendingPausedBanner() {
  const { user } = useAuth()
  const can = useCanControlSending()
  const [open, setOpen] = useState(false)
  if (!user?.sending_paused) return null
  return (
    <div
      role="status"
      data-testid="sending-paused-banner"
      className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-warning-200 bg-warning-50 px-4 py-3 text-sm text-warning-700"
    >
      <span>Sending is stopped. No customer is getting messages from RemindLoop.</span>
      {can && (
        <Button size="sm" onClick={() => setOpen(true)}>
          Resume
        </Button>
      )}
      <ResumeModal open={open} onClose={() => setOpen(false)} />
    </div>
  )
}
